// src/hooks/useInsight.ts

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Insight } from '../types/insight';
import {
  getActiveInsights,
  applyInsightSuggestion as apiApply,
  dismissInsight as apiDismiss,
} from '../services/api/insight';
import { logger } from '../utils/logger';

const SNOOZE_HOURS = 4;

const getSnoozeKey = (userId: string) => `orlo-insight-snooze-${userId}`;

/** 讀取 snooze 紀錄，過濾已過期的項目 */
function getActiveSnoozed(userId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(getSnoozeKey(userId));
    const all: Record<string, number> = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    return Object.fromEntries(Object.entries(all).filter(([, resumeAt]) => resumeAt > now));
  } catch {
    return {};
  }
}

export interface UseInsightReturn {
  insight: Insight | null;
  handleAccept: () => Promise<void>;
  handleSnooze: () => void;
  handleDismiss: () => Promise<void>;
}

export function useInsight(): UseInsightReturn {
  const { currentUser } = useAuth();
  const { showError } = useToast();

  const [insight, setInsight] = useState<Insight | null>(null);

  const loadInsight = useCallback(async () => {
    if (!currentUser) return;
    try {
      const insights = await getActiveInsights(currentUser.uid);
      const snoozed = getActiveSnoozed(currentUser.uid);
      const active = insights.find(i => !snoozed[i.id]) ?? null;
      setInsight(active);
    } catch (error) {
      logger.error('useInsight', '載入 insight 失敗', error);
      setInsight(null);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    loadInsight();
  }, [loadInsight]);

  const handleAccept = useCallback(async () => {
    if (!insight) return;
    const id = insight.id;
    setInsight(null);
    try {
      await apiApply(id);
    } catch (error) {
      logger.error('useInsight', '接受建議失敗', error);
      showError('Failed to apply suggestion. Please try again.');
      loadInsight();
    }
  }, [insight, showError, loadInsight]);

  const handleSnooze = useCallback(() => {
    if (!insight || !currentUser) return;
    const key = getSnoozeKey(currentUser.uid);
    const snoozed = getActiveSnoozed(currentUser.uid);
    snoozed[insight.id] = Date.now() + SNOOZE_HOURS * 60 * 60 * 1000;
    localStorage.setItem(key, JSON.stringify(snoozed));
    setInsight(null);
  }, [insight, currentUser]);

  const handleDismiss = useCallback(async () => {
    if (!insight) return;
    const id = insight.id;
    setInsight(null);
    try {
      await apiDismiss(id);
    } catch (error) {
      logger.error('useInsight', 'Dismiss 失敗', error);
      showError('Failed to dismiss. Please try again.');
      loadInsight();
    }
  }, [insight, showError, loadInsight]);

  return {
    insight,
    handleAccept,
    handleSnooze,
    handleDismiss
  };
}
